import { type ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAppStore } from "../store/appStore";
import DataTable, { Column } from "../components/DataTable";
import Modal from "../components/Modal";
import { IconAlert, IconLoader } from "../components/Icons";

function formatMinor(value: number, currency = "CZK"): string {
  return new Intl.NumberFormat("cs-CZ", { style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value / 100);
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("cs-CZ");
}

/** Convert a datetime-local value (browser local time) to UTC ISO string. */
function toUtcIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

const LIMIT = 25;

// ---- Branches ----
interface Branch {
  id: string;
  code: string;
  name: string;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string;
  currency: string;
  locale: string;
  phone: string | null;
  is_active: boolean;
}

function BranchesSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "branches"],
    queryFn: async () => {
      const res = await api.get<Branch[]>(`/ops/tenants/${tenantId}/branches`);
      return res.data;
    },
  });
  const columns: Column<Branch>[] = [
    { key: "code", header: "Kód", render: (r) => r.code },
    { key: "name", header: "Název", render: (r) => r.name },
    { key: "city", header: "Město", render: (r) => r.city ?? "—" },
    { key: "currency", header: "Měna", render: (r) => r.currency },
    { key: "active", header: "Aktivní", render: (r) => <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>{r.is_active ? "Ano" : "Ne"}</span> },
  ];
  return <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />;
}

// ---- Warehouses ----
interface Warehouse {
  id: string;
  code: string;
  name: string;
  address: string | null;
  city: string | null;
  is_active: boolean;
  notification_time: string | null;
  notification_emails: string[];
}

function WarehousesSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "warehouses"],
    queryFn: async () => {
      const res = await api.get<Warehouse[]>(`/ops/tenants/${tenantId}/warehouses`);
      return res.data;
    },
  });
  const [edit, setEdit] = useState<Warehouse | null>(null);
  const [time, setTime] = useState("");
  const [emails, setEmails] = useState("");

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!edit) throw new Error("No warehouse");
      await api.put(`/ops/tenants/${tenantId}/warehouses/${edit.id}/notification`, {
        notification_time: time || null,
        notification_emails: emails.split(",").map((s) => s.trim()).filter(Boolean),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ops-tenants", tenantId, "warehouses"] });
      setEdit(null);
    },
  });

  function open(w: Warehouse) {
    setEdit(w);
    setTime(w.notification_time ?? "");
    setEmails(w.notification_emails?.join(", ") ?? "");
  }

  const columns: Column<Warehouse>[] = [
    { key: "code", header: "Kód", render: (r) => r.code },
    { key: "name", header: "Název", render: (r) => r.name },
    { key: "active", header: "Aktivní", render: (r) => <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>{r.is_active ? "Ano" : "Ne"}</span> },
    { key: "emails", header: t("dataPage.notificationEmails"), render: (r) => r.notification_emails?.join(", ") || "—" },
    { key: "time", header: t("dataPage.notificationTime"), render: (r) => r.notification_time ?? "—" },
    {
      key: "actions",
      header: "",
      render: (r) => <button onClick={() => open(r)} className="ghost" style={{ padding: "6px 10px" }}>{t("dataPage.editNotification")}</button>,
    },
  ];

  return (
    <>
      <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />
      <Modal open={!!edit} onClose={() => setEdit(null)} title={t("dataPage.editNotification")}>
        <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label>{t("dataPage.notificationTime")}</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div>
            <label>{t("dataPage.notificationEmails")}</label>
            <input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="a@b.c, d@e.f" />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button type="button" onClick={() => setEdit(null)} className="ghost">{t("common.cancel")}</button>
            <button type="submit" className="primary" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? <IconLoader size={16} /> : t("dataPage.saveNotification")}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

// ---- Products ----
interface ProductVariant {
  id: string;
  name: string;
  price: number;
  is_active: boolean;
}

interface Product {
  id: string;
  name: string;
  code: string | null;
  price: number;
  unit: string;
  is_active: boolean;
  category_id: string | null;
  vat_rate_id: string | null;
  has_variants: boolean;
  variants: ProductVariant[];
}

function ProductsSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "products", q, offset],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
      if (q) params.set("q", q);
      const res = await api.get<Product[]>(`/ops/tenants/${tenantId}/catalog/products?${params.toString()}`);
      return res.data;
    },
  });
  const columns: Column<Product>[] = [
    { key: "name", header: "Název", render: (r) => r.name },
    { key: "code", header: "Kód", render: (r) => r.code ?? "—" },
    { key: "price", header: "Cena", render: (r) => formatMinor(r.price) },
    { key: "unit", header: "Jedn.", render: (r) => r.unit },
    { key: "active", header: "Aktivní", render: (r) => <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>{r.is_active ? "Ano" : "Ne"}</span> },
  ];
  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <input value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} placeholder={t("dataPage.searchProduct")} />
      </div>
      <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} disabled={offset === 0}>{t("common.previous")}</button>
        <button onClick={() => setOffset((o) => o + LIMIT)} disabled={(data ?? []).length < LIMIT}>{t("common.next")}</button>
      </div>
    </>
  );
}

// ---- Categories ----
interface Category {
  id: string;
  name: string;
  code: string | null;
  color: string | null;
  sort_order: number;
  is_active: boolean;
}

function CategoriesSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "categories"],
    queryFn: async () => {
      const res = await api.get<Category[]>(`/ops/tenants/${tenantId}/catalog/categories`);
      return res.data;
    },
  });
  const columns: Column<Category>[] = [
    { key: "name", header: "Název", render: (r) => r.name },
    { key: "code", header: "Kód", render: (r) => r.code ?? "—" },
    { key: "color", header: "Barva", render: (r) => r.color ? <span style={{ display: "inline-block", width: 16, height: 16, borderRadius: 4, background: r.color ?? "transparent" }} /> : "—" },
    { key: "active", header: "Aktivní", render: (r) => <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>{r.is_active ? "Ano" : "Ne"}</span> },
  ];
  return <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />;
}

// ---- Tags ----
interface Tag {
  id: string;
  name: string;
  color: string | null;
}

function TagsSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "tags"],
    queryFn: async () => {
      const res = await api.get<Tag[]>(`/ops/tenants/${tenantId}/catalog/tags`);
      return res.data;
    },
  });
  const columns: Column<Tag>[] = [
    { key: "name", header: "Název", render: (r) => r.name },
    { key: "color", header: "Barva", render: (r) => r.color ? <span style={{ display: "inline-block", width: 16, height: 16, borderRadius: 4, background: r.color ?? "transparent" }} /> : "—" },
  ];
  return <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />;
}

// ---- VAT rates ----
interface VatRate {
  id: string;
  code: string;
  name: string;
  rate: number;
  is_default: boolean;
  is_active: boolean;
}

function VatRatesSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "vat-rates"],
    queryFn: async () => {
      const res = await api.get<VatRate[]>(`/ops/tenants/${tenantId}/catalog/vat-rates`);
      return res.data;
    },
  });
  const columns: Column<VatRate>[] = [
    { key: "code", header: "Kód", render: (r) => r.code },
    { key: "name", header: "Název", render: (r) => r.name },
    { key: "rate", header: "Sazba", render: (r) => `${r.rate}%` },
    { key: "default", header: "Výchozí", render: (r) => r.is_default ? "Ano" : "Ne" },
    { key: "active", header: "Aktivní", render: (r) => <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>{r.is_active ? "Ano" : "Ne"}</span> },
  ];
  return <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />;
}

// ---- Orders ----
interface Order {
  id: string;
  number: string | null;
  status: string;
  total: number;
  currency: string;
  opened_at: string;
  closed_at: string | null;
  branch_id: string;
}

function OrdersSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("");
  const [branchId, setBranchId] = useState("");
  const [offset, setOffset] = useState(0);

  const { data: branches } = useQuery({
    queryKey: ["ops-tenants", tenantId, "branches"],
    queryFn: async () => {
      const res = await api.get<Branch[]>(`/ops/tenants/${tenantId}/branches`);
      return res.data;
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "orders", status, branchId, offset],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
      if (status) params.set("status", status);
      if (branchId) params.set("branch_id", branchId);
      const res = await api.get<Order[]>(`/ops/tenants/${tenantId}/orders?${params.toString()}`);
      return res.data;
    },
  });

  const columns: Column<Order>[] = [
    { key: "number", header: "Číslo", render: (r) => r.number ?? "—" },
    { key: "status", header: t("dataPage.status"), render: (r) => <span className="badge badge-accent">{r.status}</span> },
    { key: "total", header: "Celkem", render: (r) => formatMinor(r.total, r.currency) },
    { key: "opened", header: "Otevřeno", render: (r) => formatDate(r.opened_at) },
  ];

  return (
    <>
      <div className="grid grid-2" style={{ marginBottom: 16 }}>
        <div>
          <label>{t("dataPage.status")}</label>
          <select value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }}>
            <option value="">{t("dataPage.all")}</option>
            <option value="open">open</option>
            <option value="paying">paying</option>
            <option value="paid">paid</option>
            <option value="cancelled">cancelled</option>
            <option value="refunded">refunded</option>
          </select>
        </div>
        <div>
          <label>{t("dataPage.branch")}</label>
          <select value={branchId} onChange={(e) => { setBranchId(e.target.value); setOffset(0); }}>
            <option value="">{t("dataPage.all")}</option>
            {(branches ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>
      <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} disabled={offset === 0}>{t("common.previous")}</button>
        <button onClick={() => setOffset((o) => o + LIMIT)} disabled={(data ?? []).length < LIMIT}>{t("common.next")}</button>
      </div>
    </>
  );
}

// ---- Customers ----
interface Customer {
  id: string;
  name: string | null;
  company_name: string | null;
  phone: string | null;
  email: string | null;
  city: string | null;
  is_active: boolean;
}

function CustomersSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "customers", offset],
    queryFn: async () => {
      const res = await api.get<Customer[]>(`/ops/tenants/${tenantId}/customers?limit=${LIMIT}&offset=${offset}`);
      return res.data;
    },
  });
  const columns: Column<Customer>[] = [
    { key: "name", header: "Název", render: (r) => r.name ?? r.company_name ?? "—" },
    { key: "phone", header: "Telefon", render: (r) => r.phone ?? "—" },
    { key: "email", header: "E-mail", render: (r) => r.email ?? "—" },
    { key: "active", header: "Aktivní", render: (r) => <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>{r.is_active ? "Ano" : "Ne"}</span> },
  ];
  return (
    <>
      <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} disabled={offset === 0}>{t("common.previous")}</button>
        <button onClick={() => setOffset((o) => o + LIMIT)} disabled={(data ?? []).length < LIMIT}>{t("common.next")}</button>
      </div>
    </>
  );
}

// ---- Employees ----
interface Employee {
  id: string;
  full_name: string | null;
  display_name: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
}

function EmployeesSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "employees"],
    queryFn: async () => {
      const res = await api.get<Employee[]>(`/ops/tenants/${tenantId}/staff/employees`);
      return res.data;
    },
  });
  const columns: Column<Employee>[] = [
    { key: "name", header: "Jméno", render: (r) => r.display_name },
    { key: "full_name", header: "Celé jméno", render: (r) => r.full_name ?? "—" },
    { key: "role", header: "Role", render: (r) => r.role },
    { key: "active", header: "Aktivní", render: (r) => <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>{r.is_active ? "Ano" : "Ne"}</span> },
  ];
  return <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />;
}

// ---- Devices ----
interface Device {
  id: string;
  name: string;
  platform: string | null;
  app_version: string | null;
  branch_id: string | null;
  warehouse_id: string | null;
  is_active: boolean;
  last_seen_at: string | null;
}

function DevicesSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const user = useAppStore((s) => s.user);
  const [modalDevice, setModalDevice] = useState<Device | null>(null);
  const [licenseType, setLicenseType] = useState("paid");
  const [licensePeriod, setLicensePeriod] = useState("month");
  const [startsAt, setStartsAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [modalError, setModalError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "devices"],
    queryFn: async () => {
      const res = await api.get<Device[]>(`/ops/tenants/${tenantId}/devices`);
      return res.data;
    },
  });

  const setLicense = useMutation({
    mutationFn: async (deviceId: string) => {
      const payload: Record<string, string> = {
        type: licenseType,
        period: licensePeriod,
        expires_at: toUtcIso(expiresAt) || `${expiresAt}:00Z`,
      };
      const starts = toUtcIso(startsAt);
      if (starts) payload.starts_at = starts;
      const res = await api.post(`/ops/tenants/${tenantId}/devices/${deviceId}/license`, payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ops-tenants", tenantId, "devices"] });
      qc.invalidateQueries({ queryKey: ["ops-tenants", tenantId, "licenses"] });
      setModalDevice(null);
      setLicenseType("paid");
      setLicensePeriod("month");
      setStartsAt("");
      setExpiresAt("");
      setModalError(null);
    },
    onError: (e: unknown) => {
      const ae = e as { response?: { data?: { detail?: string } } };
      setModalError(ae.response?.data?.detail ?? t("dataPage.saveError"));
    },
  });

  function openModal(device: Device) {
    setLicenseType("paid");
    setLicensePeriod("month");
    setStartsAt("");
    setExpiresAt("");
    setModalError(null);
    setModalDevice(device);
  }

  const columns: Column<Device>[] = [
    { key: "name", header: "Název", render: (r) => r.name },
    { key: "platform", header: "Platforma", render: (r) => r.platform ?? "—" },
    { key: "version", header: "Verze", render: (r) => r.app_version ?? "—" },
    { key: "active", header: "Aktivní", render: (r) => <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>{r.is_active ? "Ano" : "Ne"}</span> },
    { key: "lastSeen", header: "Naposledy", render: (r) => formatDate(r.last_seen_at) },
    {
      key: "actions",
      header: "",
      render: (r) =>
        user?.role === "superadmin" ? (
          <button className="ghost" onClick={() => openModal(r)}>
            {t("dataPage.setLicense")}
          </button>
        ) : null,
    },
  ];

  return (
    <>
      <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />
      {modalDevice && (
        <Modal open={!!modalDevice} onClose={() => setModalDevice(null)} title={t("dataPage.setLicense")}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label>{t("dataPage.licenseType")}</label>
            <select value={licenseType} onChange={(e) => setLicenseType(e.target.value)}>
              <option value="paid">Paid</option>
              <option value="trial">Trial</option>
              <option value="promo">Promo</option>
              <option value="invoice">Invoice</option>
            </select>
            <label>{t("dataPage.licensePeriod")}</label>
            <select value={licensePeriod} onChange={(e) => setLicensePeriod(e.target.value)}>
              <option value="month">Month</option>
              <option value="year">Year</option>
              <option value="trial">Trial</option>
            </select>
            <label>{t("dataPage.startsAt")}</label>
            <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            <label>{t("dataPage.expiresAt")}</label>
            <input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            {modalError && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 14px",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--red-dim)",
                  color: "var(--red)",
                  fontSize: 13,
                }}
              >
                <IconAlert size={16} />
                {modalError}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 8 }}>
              <button className="ghost" onClick={() => setModalDevice(null)} disabled={setLicense.isPending}>
                {t("common.cancel")}
              </button>
              <button
                className="primary"
                disabled={!expiresAt || setLicense.isPending}
                onClick={() => setLicense.mutate(modalDevice.id)}
              >
                {setLicense.isPending ? <IconLoader size={16} className="spin" /> : t("common.save")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ---- Licenses ----
interface License {
  id: string;
  device_id: string;
  type: string;
  period: string;
  expires_at: string | null;
  is_active: boolean;
}

function LicensesSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "licenses"],
    queryFn: async () => {
      const res = await api.get<License[]>(`/ops/tenants/${tenantId}/licenses`);
      return res.data;
    },
  });
  const columns: Column<License>[] = [
    { key: "device", header: "Zařízení", render: (r) => r.device_id },
    { key: "type", header: "Typ", render: (r) => r.type },
    { key: "period", header: "Období", render: (r) => r.period },
    { key: "expires", header: "Expirace", render: (r) => r.expires_at ? formatDate(r.expires_at) : "—" },
    { key: "active", header: "Aktivní", render: (r) => <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>{r.is_active ? "Ano" : "Ne"}</span> },
  ];
  return <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />;
}

// ---- Invoices ----
interface Invoice {
  id: string;
  invoice_number: string;
  total_minor: number;
  vat_minor: number;
  currency: string;
  due_date: string | null;
  created_at: string;
}

function InvoicesSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "invoices", offset],
    queryFn: async () => {
      const res = await api.get<Invoice[]>(`/ops/tenants/${tenantId}/invoices?limit=${LIMIT}&offset=${offset}`);
      return res.data;
    },
  });
  const columns: Column<Invoice>[] = [
    { key: "number", header: "Číslo", render: (r) => r.invoice_number },
    { key: "total", header: "Celkem", render: (r) => formatMinor(r.total_minor, r.currency) },
    { key: "vat", header: "DPH", render: (r) => formatMinor(r.vat_minor, r.currency) },
    { key: "due", header: "Splatnost", render: (r) => formatDate(r.due_date) },
  ];
  return (
    <>
      <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} disabled={offset === 0}>{t("common.previous")}</button>
        <button onClick={() => setOffset((o) => o + LIMIT)} disabled={(data ?? []).length < LIMIT}>{t("common.next")}</button>
      </div>
    </>
  );
}

// ---- EET receipts ----
interface EetReceipt {
  id: string;
  order_id: string | null;
  branch_id: string;
  status: string;
  pok: string | null;
  sent_at: string | null;
  created_at: string;
}

function EetReceiptsSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "eet-receipts", offset],
    queryFn: async () => {
      const res = await api.get<EetReceipt[]>(`/ops/tenants/${tenantId}/eet-receipts?limit=${LIMIT}&offset=${offset}`);
      return res.data;
    },
  });
  const columns: Column<EetReceipt>[] = [
    { key: "status", header: "Stav", render: (r) => <span className="badge badge-accent">{r.status}</span> },
    { key: "pok", header: "POK", render: (r) => r.pok ?? "—" },
    { key: "sent", header: "Odesláno", render: (r) => formatDate(r.sent_at) },
  ];
  return (
    <>
      <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} disabled={offset === 0}>{t("common.previous")}</button>
        <button onClick={() => setOffset((o) => o + LIMIT)} disabled={(data ?? []).length < LIMIT}>{t("common.next")}</button>
      </div>
    </>
  );
}

// ---- Shifts ----
interface Shift {
  id: string;
  branch_id: string;
  employee_id: string;
  opened_at: string;
  closed_at: string | null;
  opening_cash: number;
  closing_cash: number | null;
  is_closed: boolean;
}

function ShiftsSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const [branchId, setBranchId] = useState("");
  const [offset, setOffset] = useState(0);
  const { data: branches } = useQuery({
    queryKey: ["ops-tenants", tenantId, "branches"],
    queryFn: async () => {
      const res = await api.get<Branch[]>(`/ops/tenants/${tenantId}/branches`);
      return res.data;
    },
  });
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "shifts", branchId, offset],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
      if (branchId) params.set("branch_id", branchId);
      const res = await api.get<Shift[]>(`/ops/tenants/${tenantId}/shifts?${params.toString()}`);
      return res.data;
    },
  });
  const columns: Column<Shift>[] = [
    { key: "branch", header: t("dataPage.branch"), render: (r) => r.branch_id },
    { key: "employee", header: t("dataPage.employee"), render: (r) => r.employee_id },
    { key: "opened", header: "Otevřeno", render: (r) => formatDate(r.opened_at) },
    { key: "closed", header: "Uzavřeno", render: (r) => r.is_closed ? formatDate(r.closed_at) : "—" },
    { key: "cash", header: "Hotovost", render: (r) => formatMinor(r.opening_cash) },
  ];
  return (
    <>
      <div style={{ marginBottom: 16, width: 240 }}>
        <label>{t("dataPage.branch")}</label>
        <select value={branchId} onChange={(e) => { setBranchId(e.target.value); setOffset(0); }}>
          <option value="">{t("dataPage.all")}</option>
          {(branches ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>
      <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} disabled={offset === 0}>{t("common.previous")}</button>
        <button onClick={() => setOffset((o) => o + LIMIT)} disabled={(data ?? []).length < LIMIT}>{t("common.next")}</button>
      </div>
    </>
  );
}

// ---- Attendance ----
interface Attendance {
  id: string;
  employee_id: string;
  branch_id: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
}

function AttendanceSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const [branchId, setBranchId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [offset, setOffset] = useState(0);
  const { data: branches } = useQuery({
    queryKey: ["ops-tenants", tenantId, "branches"],
    queryFn: async () => {
      const res = await api.get<Branch[]>(`/ops/tenants/${tenantId}/branches`);
      return res.data;
    },
  });
  const { data: employees } = useQuery({
    queryKey: ["ops-tenants", tenantId, "employees"],
    queryFn: async () => {
      const res = await api.get<Employee[]>(`/ops/tenants/${tenantId}/staff/employees`);
      return res.data;
    },
  });
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "attendance", branchId, employeeId, offset],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
      if (branchId) params.set("branch_id", branchId);
      if (employeeId) params.set("employee_id", employeeId);
      const res = await api.get<Attendance[]>(`/ops/tenants/${tenantId}/attendance?${params.toString()}`);
      return res.data;
    },
  });
  const columns: Column<Attendance>[] = [
    { key: "employee", header: t("dataPage.employee"), render: (r) => r.employee_id },
    { key: "branch", header: t("dataPage.branch"), render: (r) => r.branch_id },
    { key: "started", header: "Začátek", render: (r) => formatDate(r.started_at) },
    { key: "ended", header: "Konec", render: (r) => formatDate(r.ended_at) },
    { key: "duration", header: "Délka", render: (r) => r.duration_seconds ? `${Math.round(r.duration_seconds / 60)} min` : "—" },
  ];
  return (
    <>
      <div className="grid grid-2" style={{ marginBottom: 16 }}>
        <div>
          <label>{t("dataPage.branch")}</label>
          <select value={branchId} onChange={(e) => { setBranchId(e.target.value); setOffset(0); }}>
            <option value="">{t("dataPage.all")}</option>
            {(branches ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label>{t("dataPage.employee")}</label>
          <select value={employeeId} onChange={(e) => { setEmployeeId(e.target.value); setOffset(0); }}>
            <option value="">{t("dataPage.all")}</option>
            {(employees ?? []).map((e) => <option key={e.id} value={e.id}>{e.display_name}</option>)}
          </select>
        </div>
      </div>
      <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} disabled={offset === 0}>{t("common.previous")}</button>
        <button onClick={() => setOffset((o) => o + LIMIT)} disabled={(data ?? []).length < LIMIT}>{t("common.next")}</button>
      </div>
    </>
  );
}

// ---- Inventory ----
interface Inventory {
  product_id: string;
  warehouse_id: string;
  quantity: number;
  min_quantity: number | null;
}

function InventorySection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const [warehouseId, setWarehouseId] = useState("");
  const [offset, setOffset] = useState(0);
  const { data: warehouses } = useQuery({
    queryKey: ["ops-tenants", tenantId, "warehouses"],
    queryFn: async () => {
      const res = await api.get<Warehouse[]>(`/ops/tenants/${tenantId}/warehouses`);
      return res.data;
    },
  });
  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "inventory", warehouseId, offset],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
      if (warehouseId) params.set("warehouse_id", warehouseId);
      const res = await api.get<Inventory[]>(`/ops/tenants/${tenantId}/inventory?${params.toString()}`);
      return res.data;
    },
  });
  const columns: Column<Inventory>[] = [
    { key: "product", header: "Produkt", render: (r) => r.product_id },
    { key: "warehouse", header: t("dataPage.warehouse"), render: (r) => r.warehouse_id },
    { key: "quantity", header: "Množství", render: (r) => r.quantity },
    { key: "min", header: "Minimum", render: (r) => r.min_quantity ?? "—" },
  ];
  return (
    <>
      <div style={{ marginBottom: 16, width: 240 }}>
        <label>{t("dataPage.warehouse")}</label>
        <select value={warehouseId} onChange={(e) => { setWarehouseId(e.target.value); setOffset(0); }}>
          <option value="">{t("dataPage.all")}</option>
          {(warehouses ?? []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </div>
      <DataTable rows={data ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} disabled={offset === 0}>{t("common.previous")}</button>
        <button onClick={() => setOffset((o) => o + LIMIT)} disabled={(data ?? []).length < LIMIT}>{t("common.next")}</button>
      </div>
    </>
  );
}

// ---- Stock receipts ----
interface StockReceipt {
  id: string;
  supplier_id: string | null;
  warehouse_id: string;
  reference: string | null;
  status: string;
  total: number;
  created_at: string;
}

interface StockReceiptItem {
  id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  total: number;
}

function StockReceiptsSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const { data: receipts, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", tenantId, "stock-receipts", offset],
    queryFn: async () => {
      const res = await api.get<StockReceipt[]>(`/ops/tenants/${tenantId}/stock-receipts?limit=${LIMIT}&offset=${offset}`);
      return res.data;
    },
  });
  const { data: items } = useQuery({
    queryKey: ["ops-tenants", tenantId, "stock-receipts", expanded, "items"],
    queryFn: async () => {
      if (!expanded) return [];
      const res = await api.get<StockReceiptItem[]>(`/ops/tenants/${tenantId}/stock-receipts/${expanded}/items`);
      return res.data;
    },
    enabled: !!expanded,
  });
  const columns: Column<StockReceipt>[] = [
    { key: "reference", header: "Doklad", render: (r) => r.reference ?? "—" },
    { key: "warehouse", header: t("dataPage.warehouse"), render: (r) => r.warehouse_id },
    { key: "status", header: "Stav", render: (r) => <span className="badge badge-accent">{r.status}</span> },
    { key: "total", header: "Celkem", render: (r) => formatMinor(r.total) },
    { key: "created", header: "Vytvořeno", render: (r) => formatDate(r.created_at) },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <button onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="ghost" style={{ padding: "6px 10px" }}>
          {expanded === r.id ? t("common.close") : t("dataPage.items")}
        </button>
      ),
    },
  ];
  return (
    <>
      <DataTable rows={receipts ?? []} columns={columns} loading={isLoading} error={error ? t("dataPage.loadError") : null} emptyMessage={t("dataPage.noData")} />
      {expanded && (
        <div className="card" style={{ marginTop: 16 }}>
          <h4 style={{ marginBottom: 12 }}>{t("dataPage.items")}</h4>
          <table>
            <thead>
              <tr>
                <th>Produkt</th>
                <th>Množství</th>
                <th>Cena/jedn.</th>
                <th>Celkem</th>
              </tr>
            </thead>
            <tbody>
              {(items ?? []).map((it) => (
                <tr key={it.id}>
                  <td>{it.product_id}</td>
                  <td>{it.quantity}</td>
                  <td>{formatMinor(it.unit_price)}</td>
                  <td>{formatMinor(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} disabled={offset === 0}>{t("common.previous")}</button>
        <button onClick={() => setOffset((o) => o + LIMIT)} disabled={(receipts ?? []).length < LIMIT}>{t("common.next")}</button>
      </div>
    </>
  );
}

const SECTIONS: Record<string, (tenantId: string) => ReactNode> = {
  branches: (id) => <BranchesSection tenantId={id} />,
  warehouses: (id) => <WarehousesSection tenantId={id} />,
  products: (id) => <ProductsSection tenantId={id} />,
  categories: (id) => <CategoriesSection tenantId={id} />,
  tags: (id) => <TagsSection tenantId={id} />,
  "vat-rates": (id) => <VatRatesSection tenantId={id} />,
  orders: (id) => <OrdersSection tenantId={id} />,
  customers: (id) => <CustomersSection tenantId={id} />,
  employees: (id) => <EmployeesSection tenantId={id} />,
  devices: (id) => <DevicesSection tenantId={id} />,
  licenses: (id) => <LicensesSection tenantId={id} />,
  invoices: (id) => <InvoicesSection tenantId={id} />,
  "eet-receipts": (id) => <EetReceiptsSection tenantId={id} />,
  shifts: (id) => <ShiftsSection tenantId={id} />,
  attendance: (id) => <AttendanceSection tenantId={id} />,
  inventory: (id) => <InventorySection tenantId={id} />,
  "stock-receipts": (id) => <StockReceiptsSection tenantId={id} />,
};

export default function TenantDataPage() {
  const { id, section } = useParams<{ id: string; section: string }>();
  const { t } = useTranslation();
  const setTenantId = useAppStore((s) => s.setTenantId);

  useEffect(() => {
    if (id) setTenantId(id);
  }, [id, setTenantId]);

  if (!id || !section || !SECTIONS[section]) {
    return <div className="card" style={{ padding: 32, color: "var(--muted)" }}>{t("dataPage.noData")}</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t(`nav.${section.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())}` as string)}</h1>
      </div>
      <div className="card">
        {SECTIONS[section](id)}
      </div>
    </div>
  );
}
